import React, {useRef, useState} from "react";
import {Box, Button, IconButton, Stack, TextField, Tooltip, Typography} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import InviteMemberButton from "./InviteMemberButton";
import {KIOSK_PHOTO_TYPES, LIMITS} from '../../contract';
import {MembersList} from "./BoardMemberList";
import {PendingInvitations} from "./PendingInvitations";
import {SettingsCard} from "../layouts/SettingsCard";
import {CalendarFeedsCard} from "./CalendarFeedsCard";
import {useMembers} from '../../data/members';
import {uploadErrorMessage, useDeleteKioskPhoto, useKioskPhotos, useUploadKioskPhoto} from '../../data/kioskPhotos';
import {useUpdateArchiveDays, useUpdateKioskMessage, useUpdateKioskPhotos} from '../../data/board';

// The same limits the API enforces (src/contract).
const MAX_ARCHIVE_AFTER_DAYS = LIMITS.archiveAfterDays;
const MAX_KIOSK_MESSAGE_LENGTH = LIMITS.kioskMessage;
const MAX_KIOSK_PHOTOS = LIMITS.kioskPhotoLinks;
const MAX_KIOSK_PHOTOS_UPLOADED = LIMITS.kioskPhotoUploads;
const MAX_KIOSK_PHOTO_BYTES = LIMITS.kioskPhotoBytes;
const ALLOWED_KIOSK_PHOTO_TYPES: readonly string[] = KIOSK_PHOTO_TYPES;

export function BoardManager({ boardId, canManageMembers, canEdit = false, sections = [], kioskMessage, kioskPhotoUrls = [], archiveAfterDays }: { boardId: string, canManageMembers: boolean, canEdit?: boolean, sections?: { id: string, title: string }[], kioskMessage?: string, kioskPhotoUrls?: string[], archiveAfterDays?: number }) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [draftMessage, setDraftMessage] = useState(kioskMessage ?? "");
    const [newPhotoUrl, setNewPhotoUrl] = useState("");
    const [photoUrlError, setPhotoUrlError] = useState("");
    const [uploadError, setUploadError] = useState("");
    const [draftArchiveDays, setDraftArchiveDays] = useState(archiveAfterDays === undefined ? "" : String(archiveAfterDays));

    const {data: uploadedPhotos = []} = useKioskPhotos(boardId, {enabled: canManageMembers});
    const {data: members, isLoading} = useMembers(boardId);

    const parsedArchiveDays = /^\d+$/.test(draftArchiveDays) ? Number(draftArchiveDays) : NaN;
    const isArchiveDaysValid = parsedArchiveDays >= 0 && parsedArchiveDays <= MAX_ARCHIVE_AFTER_DAYS;

    const {mutate: saveKioskMessage, isPending: isSavingKioskMessage} = useUpdateKioskMessage(boardId);
    const {mutate: saveArchiveDays, isPending: isSavingArchiveDays, isError: archiveDaysFailed} = useUpdateArchiveDays(boardId);
    const {mutate: savePhotoUrls} = useUpdateKioskPhotos(boardId);

    function handleAddPhoto() {
        const url = newPhotoUrl.trim();

        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            setPhotoUrlError('Must be a full http(s) URL');
            return;
        }
        if (kioskPhotoUrls.length >= MAX_KIOSK_PHOTOS) {
            setPhotoUrlError(`You can add up to ${MAX_KIOSK_PHOTOS} photos`);
            return;
        }

        setPhotoUrlError("");
        savePhotoUrls([...kioskPhotoUrls, url]);
        setNewPhotoUrl("");
    }

    function handleRemovePhoto(url: string) {
        savePhotoUrls(kioskPhotoUrls.filter((u) => u !== url));
    }

    const {mutate: uploadPhoto, isPending: isUploadingPhoto} = useUploadKioskPhoto(boardId);
    const {mutate: deletePhoto} = useDeleteKioskPhoto(boardId);

    function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting the same file later
        if (!file) return;

        setUploadError("");

        if (!ALLOWED_KIOSK_PHOTO_TYPES.includes(file.type)) {
            setUploadError('Must be a JPEG, PNG, GIF, or WebP image');
            return;
        }
        if (file.size > MAX_KIOSK_PHOTO_BYTES) {
            setUploadError(`Photo must be at most ${MAX_KIOSK_PHOTO_BYTES / (1024 * 1024)}MB`);
            return;
        }
        if (uploadedPhotos.length >= MAX_KIOSK_PHOTOS_UPLOADED) {
            setUploadError(`You can upload up to ${MAX_KIOSK_PHOTOS_UPLOADED} photos`);
            return;
        }

        uploadPhoto(file, {onError: (error) => setUploadError(uploadErrorMessage(error))});
    }

    const photoCount = kioskPhotoUrls.length + uploadedPhotos.length;

    // A tile with a corner delete button, for one photo in the carousel.
    const photoTile = (key: string, src: string, alt: string, onDelete: () => void) => (
        <Box key={key} sx={{position: 'relative', aspectRatio: '1', borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'divider', bgcolor: 'grey.100'}}>
            <Box component="img" src={src} alt={alt} sx={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}}/>
            <Tooltip title="Remove photo">
                <IconButton
                    size="small"
                    aria-label="Remove photo"
                    onClick={onDelete}
                    sx={{position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper', '&:hover': {bgcolor: 'background.paper', color: 'error.main'}}}
                >
                    <DeleteIcon fontSize="small"/>
                </IconButton>
            </Tooltip>
        </Box>
    );

    return (
        <Box
            sx={{
                display: 'grid',
                gap: 3,
                alignItems: 'start',
                // Owners get settings beside the member list; everyone else just sees the members, full width.
                gridTemplateColumns: canManageMembers ? {xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.2fr) minmax(0, 1fr)'} : 'minmax(0, 1fr)',
            }}
        >
            <SettingsCard
                title="Members"
                description="People with access to this board, and what they can do. Anyone you invite joins once they accept."
                action={canManageMembers ? <InviteMemberButton boardId={boardId} /> : undefined}
            >
                {!isLoading && (
                    <MembersList members={members} boardId={boardId} canManageMembers={canManageMembers} />
                )}
                {canManageMembers && <PendingInvitations boardId={boardId} />}
            </SettingsCard>

            {canManageMembers && (
                <Stack spacing={3}>
                    <SettingsCard
                        title="Archiving"
                        description="Completed tasks are archived automatically this many days after they're completed. Use 0 to never archive automatically. Archived tasks can be restored from the Archive tab."
                    >
                        <Box sx={{display: 'flex', gap: 1, alignItems: 'flex-start'}}>
                            <TextField
                                size="small"
                                label="Days after completion"
                                value={draftArchiveDays}
                                onChange={(e) => setDraftArchiveDays(e.target.value.trim())}
                                error={draftArchiveDays !== "" && !isArchiveDaysValid}
                                helperText={draftArchiveDays !== "" && !isArchiveDaysValid ? `Whole number from 0 to ${MAX_ARCHIVE_AFTER_DAYS}` : undefined}
                                inputProps={{inputMode: 'numeric'}}
                            />
                            <Button
                                variant="contained"
                                sx={{height: 40}}
                                disabled={isSavingArchiveDays || !isArchiveDaysValid || parsedArchiveDays === archiveAfterDays}
                                onClick={() => saveArchiveDays(parsedArchiveDays)}
                            >
                                Save
                            </Button>
                        </Box>
                        {archiveDaysFailed && (
                            <Typography variant="caption" color="error" display="block" sx={{mt: 0.5}}>
                                Couldn't save the archive setting. Try again.
                            </Typography>
                        )}
                    </SettingsCard>

                    <SettingsCard
                        title="Kiosk sticky note"
                        description="A message for everyone using the household display."
                    >
                        <TextField
                            label="Household message"
                            fullWidth
                            multiline
                            minRows={2}
                            value={draftMessage}
                            onChange={(e) => setDraftMessage(e.target.value.slice(0, MAX_KIOSK_MESSAGE_LENGTH))}
                            helperText={`${draftMessage.length}/${MAX_KIOSK_MESSAGE_LENGTH}`}
                        />
                        <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 1}}>
                            <Button
                                variant="contained"
                                disabled={isSavingKioskMessage || draftMessage === (kioskMessage ?? "")}
                                onClick={() => saveKioskMessage(draftMessage)}
                            >
                                Save
                            </Button>
                        </Box>
                    </SettingsCard>
                </Stack>
            )}

            {canManageMembers && (
                <Box sx={{gridColumn: '1 / -1'}}>
                    <SettingsCard
                        title="Kiosk photo carousel"
                        description={`Photos cycle on the kiosk page below the week strip. Upload your own (up to ${MAX_KIOSK_PHOTOS_UPLOADED}, ${MAX_KIOSK_PHOTO_BYTES / (1024 * 1024)}MB each; stored privately and shown through short-lived links only people with access to this board get) or link an externally hosted image (up to ${MAX_KIOSK_PHOTOS}).`}
                    >
                        <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 2}}>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ALLOWED_KIOSK_PHOTO_TYPES.join(',')}
                                onChange={handleFileSelected}
                                style={{display: 'none'}}
                            />
                            <Box>
                                <Button
                                    variant="outlined"
                                    sx={{height: 40}}
                                    disabled={isUploadingPhoto || uploadedPhotos.length >= MAX_KIOSK_PHOTOS_UPLOADED}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {isUploadingPhoto ? 'Uploading…' : 'Upload photo'}
                                </Button>
                                {uploadError && (
                                    <Typography variant="caption" color="error" display="block" sx={{mt: 0.5}}>
                                        {uploadError}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{display: 'flex', gap: 1, alignItems: 'flex-start', flex: 1, minWidth: 260, maxWidth: 520}}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Or link an image URL"
                                    value={newPhotoUrl}
                                    onChange={(e) => {
                                        setNewPhotoUrl(e.target.value);
                                        setPhotoUrlError("");
                                    }}
                                    error={!!photoUrlError}
                                    helperText={photoUrlError || " "}
                                />
                                <Button variant="contained" sx={{height: 40}} onClick={handleAddPhoto} disabled={!newPhotoUrl.trim()}>
                                    Add
                                </Button>
                            </Box>
                        </Box>

                        {photoCount === 0 ? (
                            <Typography variant="body2" color="text.secondary">No photos yet.</Typography>
                        ) : (
                            <Box sx={{display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))'}}>
                                {uploadedPhotos.map((photo) =>
                                    photoTile(`upload-${photo.id}`, photo.url, 'Uploaded kiosk photo', () => deletePhoto(photo.id)))}
                                {kioskPhotoUrls.map((url) =>
                                    photoTile(`url-${url}`, url, 'Linked kiosk photo', () => handleRemovePhoto(url)))}
                            </Box>
                        )}
                    </SettingsCard>
                </Box>
            )}

            {/* Owners and editors: events from a Google Calendar become tasks (the server checks the role again). */}
            {canEdit && (
                <Box sx={{gridColumn: '1 / -1'}}>
                    <CalendarFeedsCard boardId={boardId} sections={sections}/>
                </Box>
            )}
        </Box>
    );
}
